import { useCallback, useEffect, useRef, useState } from 'react'

import type { PricingTier } from '@/config/marketing-content/pricing'
import { PricingTierCard } from '@/components/marketing/pricing-tier-card'
import { cn } from '@/lib/utils'

/**
 * Pricing tiers as a 3-up grid on desktop (and stacked on small landscape),
 * and a swipeable, center-snapping carousel on phones + portrait tablets.
 * The centered card is raised so the neighbours' tops sit slightly lower.
 *
 * Layout is entirely CSS-driven (see `.pricing-carousel` in globals.css); the
 * JS here only runs in carousel mode — it tracks the centered card to raise it
 * and drive the dots, and opens centered on the middle (highlighted) tier.
 */
const CAROUSEL_QUERY = '(max-width: 1023px) and (orientation: portrait)'

export function PricingTierCarousel({ tiers }: { tiers: PricingTier[] }) {
  const middleIndex = Math.floor(Math.max(tiers.length - 1, 0) / 2)
  const [activeIndex, setActiveIndex] = useState(middleIndex)
  const [isCarousel, setIsCarousel] = useState(false)
  const viewportRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<(HTMLElement | null)[]>([])
  const didInitialScrollRef = useRef(false)
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )

  const scrollToIndex = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth') => {
      const clamped = Math.max(0, Math.min(tiers.length - 1, index))
      cardRefs.current[clamped]?.scrollIntoView({
        behavior: prefersReducedMotion.current ? 'auto' : behavior,
        block: 'nearest',
        inline: 'center',
      })
    },
    [tiers.length]
  )

  // Track which media (carousel vs grid) is active so we only wire up carousel
  // behaviour on phones / portrait tablets.
  useEffect(() => {
    const mq = window.matchMedia(CAROUSEL_QUERY)
    const update = () => setIsCarousel(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  // Scroll-linked: write each card's proximity to the viewport centre as a
  // --card-t (0 = off-centre, 1 = centred). CSS maps it to scale + lift, so a
  // card grows/rises continuously as it slides in. Also picks the dots' active
  // index. No-op in grid mode (the viewport is `display: contents`, never
  // scrolls) — cards keep --card-t: 0 and the media query drops the transform.
  const syncFromScroll = useCallback(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    const cards = cardRefs.current
    const viewportCenter = viewport.scrollLeft + viewport.clientWidth / 2
    // Distance between adjacent card centres (card width + gap) = the span over
    // which a card fades from centred (1) to fully off-centre (0).
    const slot =
      cards[0] && cards[1]
        ? Math.abs(cards[1].offsetLeft - cards[0].offsetLeft)
        : cards[0]?.offsetWidth ?? 1

    let closest = 0
    let closestDistance = Number.POSITIVE_INFINITY
    cards.forEach((card, index) => {
      if (!card) return
      const cardCenter = card.offsetLeft + card.offsetWidth / 2
      const distance = Math.abs(viewportCenter - cardCenter)
      const t = Math.max(0, Math.min(1, 1 - distance / slot))
      card.style.setProperty('--card-t', t.toFixed(3))
      if (distance < closestDistance) {
        closestDistance = distance
        closest = index
      }
    })
    setActiveIndex(closest)
  }, [])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    let frame: number | undefined
    const onScroll = () => {
      if (frame) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(syncFromScroll)
    }

    viewport.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      viewport.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [syncFromScroll])

  // Open the carousel centered on the middle tier, then prime the transforms.
  useEffect(() => {
    if (!isCarousel || didInitialScrollRef.current) return
    didInitialScrollRef.current = true
    setActiveIndex(middleIndex)
    scrollToIndex(middleIndex, 'auto')
    requestAnimationFrame(syncFromScroll)
  }, [isCarousel, middleIndex, scrollToIndex, syncFromScroll])

  return (
    <div
      className="pricing-carousel"
      aria-roledescription={isCarousel ? 'carousel' : undefined}
      aria-label={isCarousel ? 'Pricing plans' : undefined}
    >
      <div
        ref={viewportRef}
        className="pricing-carousel__viewport"
        tabIndex={isCarousel ? 0 : undefined}
        onKeyDown={
          isCarousel
            ? (event) => {
                if (event.key === 'ArrowLeft') {
                  event.preventDefault()
                  scrollToIndex(activeIndex - 1)
                }
                if (event.key === 'ArrowRight') {
                  event.preventDefault()
                  scrollToIndex(activeIndex + 1)
                }
              }
            : undefined
        }
      >
        <div className="pricing-carousel__track">
          {tiers.map((tier, index) => (
            <PricingTierCard
              key={tier.name}
              tier={tier}
              className={cn(index === activeIndex && 'pricing-tier-card--active')}
              cardRef={(node) => {
                cardRefs.current[index] = node
              }}
            />
          ))}
        </div>
      </div>

      <div className="pricing-carousel__dots" role="tablist" aria-label="Pricing plans">
        {tiers.map((tier, index) => (
          <button
            key={tier.name}
            type="button"
            role="tab"
            className={cn(
              'pricing-carousel__dot',
              index === activeIndex && 'pricing-carousel__dot--active'
            )}
            aria-label={`Show the ${tier.name} plan`}
            aria-selected={index === activeIndex}
            onClick={() => scrollToIndex(index)}
          />
        ))}
      </div>
    </div>
  )
}
